// lib/models/module.ts

// #75 course lifecycle. A course is created as a draft (invisible to
// candidates), the employer publishes it to make it usable, and can later
// archive it (retired — existing learners keep access) or re-open an archived
// course. Legal transitions: draft→published, draft→archived,
// published→archived, archived→published. See COURSE_LIFECYCLE_DESIGN.md.
export type CourseStatus = 'draft' | 'published' | 'archived';

// #75 distribution. program_only: reaches candidates only via a program
// assignment or job prerequisite. self_serve: also discoverable in "Fler
// moduler". The self_serve employer toggle is HIDDEN in v1 (built but not
// exposed) — new org courses are always program_only for now.
export type CourseVisibility = 'program_only' | 'self_serve';

export const COURSE_STATUS_LABEL: Record<CourseStatus, string> = {
    draft: 'Utkast',
    published: 'Publicerad',
    archived: 'Arkiverad',
};

// Legal next-states, mirroring the backend CourseStatusTransitions graph.
export const COURSE_STATUS_TRANSITIONS: Record<CourseStatus, CourseStatus[]> = {
    draft: ['published', 'archived'],
    published: ['archived'],
    archived: ['published'],
};

export const canTransitionCourseStatus = (from: CourseStatus, to: CourseStatus): boolean =>
    from === to || (COURSE_STATUS_TRANSITIONS[from]?.includes(to) ?? false);

export interface Module {
    id: string;
    module_name: string;
    module_description: string;
    case_ids: string[];
    module_type: string;
    is_mandatory: boolean;
    estimated_duration?: string;
    thumbnail_url?: string;
    status?: CourseStatus;         // #75 draft | published | archived
    visibility?: CourseVisibility; // #75 program_only | self_serve
    organisation_id?: string;
    organisation_name?: string;      // #40; nil for global modules
    organisation_logo_url?: string;  // #40; absent → name-derived placeholder
    created_at: string;
    created_by: string;
    updated_at: string;
    updated_by: string;
}

export interface Case {
    id: string;
    focus_area: string;
    difficulty_level: string;
    target_audience: string;
    case_title: string;
    case_description: string;
    questions: any; // JSON object
    metadata?: CaseMetadata;
    created_at: string;
    created_by: string;
    updated_at: string;
    updated_by: string;
}

export interface ModuleWithCases extends Module {
    cases: Case[];
}

// Request types
export interface CreateModuleRequest {
    module_name: string;
    module_description: string;
    case_ids: string[];
}

export interface UpdateModuleRequest {
    module_name?: string;
    module_description?: string;
    case_ids?: string[];
}

export interface CreateCaseRequest {
    focus_area: string;
    difficulty_level: string;
    target_audience: string;
    case_title: string;
    case_description: string;
    questions: any;
}

export interface UpdateCaseRequest {
    focus_area?: string;
    difficulty_level?: string;
    target_audience?: string;
    case_title?: string;
    case_description?: string;
    questions?: any;
}

// Re-export PaginationParams from jasit-api-kit as PaginationRequest for backwards compatibility
export type { PaginationParams as PaginationRequest } from 'jasit-api-kit';

// Enhanced UIModule interface for lib/models/module.ts
// Add these properties to your existing UIModule interface:

export interface UIModule {
    id: string;
    title: string;
    description: string;
    instructor?: string;
    category?: string;
    progress?: number;
    cases?: Case[];

    // Additional UI-specific properties:
    duration?: string;          // e.g., "4 weeks", "2 hours"
    startDate?: string;         // e.g., "2024-01-15"
    endDate?: string;           // e.g., "2024-02-15"
    difficulty?: string;        // e.g., "Beginner", "Intermediate", "Advanced"
    tags?: string[];           // Array of tags
    thumbnailUrl?: string;     // Module image/thumbnail
    enrolled?: boolean;        // Whether user is enrolled
    rating?: number;           // Module rating (1-5)
    candidatesCount?: number;    // Number of enrolled candidates
}

// You might also want to update the conversion function:
export const convertModuleToUIModule = (
    module: Module,
    cases?: Case[],
    userProgress?: number,
    additionalUIData?: Partial<UIModule>
): UIModule => ({
    id: module.id,
    title: module.module_name,
    description: module.module_description,
    instructor: module.created_by, // or map from a separate instructor field
    category: 'General', // You might want to add category to the backend Module model
    progress: userProgress || 0,
    cases: cases,
    duration: additionalUIData?.duration || 'Self-paced',
    difficulty: additionalUIData?.difficulty || 'Intermediate',
    ...additionalUIData
});

// Utility function to convert Case to a more UI-friendly format
export const convertCaseToUIFormat = (case_: Case) => ({
    ...case_,
    title: case_.case_title,
    description: case_.case_description,
});

// ============================================================================
// Module Steps
// ============================================================================

// What the candidate does in this step.
export type StepType = 'info' | 'task' | 'checkpoint' | 'confirmation' | 'quiz' | 'case' | 'file_review';

// How completion is judged. Orthogonal to StepType.
//   - self_check    → candidate ticks "done" (checklist-style)
//   - ai_eval       → candidate submits answer, AI grades
//   - manual_verify → employer/responsible person verifies
export type EvaluationMode = 'self_check' | 'ai_eval' | 'manual_verify';

export interface ModuleStep {
    id: string;
    module_id: string;
    step_order: number;
    step_type: StepType;
    step_title: string;
    content: any; // JSONB — shape depends on step_type
    evaluation_mode: EvaluationMode;
    sub_points: string[]; // Optional bullet sub-points shown under the title
    phase?: string; // Optional grouping label; consecutive same-phase steps render under one section header
    created_at: string;
    updated_at: string;
}

// ============================================================================
// Candidate Module Progress
// ============================================================================

export type ProgressStatus = 'not_started' | 'in_progress' | 'completed';

export interface CandidateModuleProgress {
    id: string;
    user_id: string;
    module_id: string;
    current_step_order: number;
    status: ProgressStatus;
    step_results: Record<string, any>;
    completed_at?: string;
    created_at: string;
    updated_at: string;
}

// ============================================================================
// Step Submission / Result
// ============================================================================

export interface SubmitStepResultRequest {
    step_order: number;
    answer: any;
}

export interface StepResultResponse {
    correct: boolean;
    explanation?: string;
    correct_answer?: string;
    step_completed: boolean;
    module_completed: boolean;
    wrong_questions?: number[];
    ai_feedback?: AIFeedback;
    next_step_order?: number;
}

export interface AIFeedback {
    assessment: string;
    strengths: string[];
    improvements: string[];
    score?: number;
}

// ============================================================================
// Answer feedback (#294)
// ============================================================================

/** The feedback keys a checkpoint's content — or a single quiz question — can
 *  carry. `explanation` is the original single field and stays the fallback for
 *  BOTH outcomes, so every course authored before the split keeps working
 *  unchanged. The two outcome-specific keys are additive. */
export interface ExplanationContent {
    explanation?: string;
    explanation_correct?: string;
    explanation_incorrect?: string;
}

/** Pick the explanation to show for an outcome.
 *
 *  ONE rule, stated once, because it is applied in five places — the checkpoint
 *  renderer, the quiz renderer, the employer's "Visa som student" preview, the
 *  backend validator, and Link's markdown round-trip. Re-deriving it per site is
 *  how the outcome-specific text ends up showing on the wrong outcome.
 *
 *  Blank-but-present counts as absent: an author who fills in only "vid fel svar"
 *  should still get the general explanation on a correct answer, not silence. */
export function explanationFor(content: ExplanationContent | undefined | null, correct: boolean): string | undefined {
    if (!content) return undefined;
    const specific = correct ? content.explanation_correct : content.explanation_incorrect;
    if (specific && specific.trim()) return specific;
    return content.explanation && content.explanation.trim() ? content.explanation : undefined;
}

// ============================================================================
// Intro Status
// ============================================================================

export interface IntroStatus {
    intro_completed: boolean;
    intro_module_id?: string;
}

// ============================================================================
// Full Module Response (module + steps + cases + progress)
// ============================================================================

// Note: Backend embeds ModuleResponse (Go struct embedding = flattened JSON),
// so module fields are at the top level alongside steps/cases/progress.
export interface ModuleFullResponse extends Module {
    steps: ModuleStep[];
    cases: Record<string, Case>;
    progress?: CandidateModuleProgress;
}

// ============================================================================
// Case Metadata
// ============================================================================

export interface CaseMetadata {
    competencies?: string[];
    parts?: CasePart[];
    scenario?: {
        available_documents?: { name: string; available: boolean }[];
    };
    ai_feedback_config?: {
        criteria?: string[];
        rubric?: string;
    };
    assessment_levels?: { level: number; label: string; color: string }[];
}

export interface CasePart {
    part_id: string;
    part_title: string;
    question_ids: number[];
}

export type QuestionType = 'multiple_choice' | 'multi_select' | 'text_input' | 'ai_evaluated' | 'confirmation';






